// Browser-compatible logger service for Studio1 Alpha Frontend
// Implements structured logging as per architecture standards
/* eslint-disable no-console */

/// <reference types="vite/client" />

// Browser-compatible logger that mimics Winston's interface
const createBrowserLogger = () => {
  const formatMessage = (level: string, message: string, context?: any) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      service: "frontend-alpha",
      correlationId: context?.correlationId || "unknown",
      message,
      metadata: context ? { ...context } : undefined,
    };
    return logEntry;
  };

  return {
    debug: (message: string, context?: any) => {
      if (import.meta.env?.DEV) {
        console.debug("[DEBUG]", formatMessage("debug", message, context));
      }
    },
    info: (message: string, context?: any) => {
      console.info("[INFO]", formatMessage("info", message, context));
    },
    warn: (message: string, context?: any) => {
      console.warn("[WARN]", formatMessage("warn", message, context));
    },
    error: (message: string, context?: any) => {
      console.error("[ERROR]", formatMessage("error", message, context));
    },
  };
};

// Create browser-compatible logger instance
const browserLogger = createBrowserLogger();

// Enhanced logger interface with correlation ID support
export interface LogContext {
  correlationId?: string;
  userId?: string;
  teamId?: string;
  requestSource?: string;
  [key: string]: any;
}

class Logger {
  private logger: any;

  constructor() {
    this.logger = browserLogger;
  }

  debug(message: string, context?: LogContext): void {
    this.logger.debug(message, context);
  }

  info(message: string, context?: LogContext): void {
    this.logger.info(message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.logger.warn(message, context);
  }

  error(message: string, error?: Error, context?: LogContext): void {
    this.logger.error(message, {
      ...context,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : undefined,
    });
  }

  // Workflow-specific logging methods
  workflowStarted(workflowId: string, context?: LogContext): void {
    this.info("Workflow execution started", {
      ...context,
      workflowId,
      event: "workflow_started",
    });
  }

  workflowCompleted(
    workflowId: string,
    duration: number,
    context?: LogContext,
  ): void {
    this.info("Workflow execution completed", {
      ...context,
      workflowId,
      duration,
      event: "workflow_completed",
    });
  }

  workflowFailed(workflowId: string, error: Error, context?: LogContext): void {
    this.error("Workflow execution failed", error, {
      ...context,
      workflowId,
      event: "workflow_failed",
    });
  }

  // Node-specific logging methods
  nodeExecutionStarted(
    nodeId: string,
    nodeType: string,
    context?: LogContext,
  ): void {
    this.info("Node execution started", {
      ...context,
      nodeId,
      nodeType,
      event: "node_execution_started",
    });
  }

  nodeExecutionCompleted(
    nodeId: string,
    nodeType: string,
    duration: number,
    context?: LogContext,
  ): void {
    this.info("Node execution completed", {
      ...context,
      nodeId,
      nodeType,
      duration,
      event: "node_execution_completed",
    });
  }

  nodeExecutionFailed(
    nodeId: string,
    nodeType: string,
    error: Error,
    context?: LogContext,
  ): void {
    this.error("Node execution failed", error, {
      ...context,
      nodeId,
      nodeType,
      event: "node_execution_failed",
    });
  }

  // API request logging
  apiRequest(method: string, url: string, context?: LogContext): void {
    this.debug("API request initiated", {
      ...context,
      method,
      url,
      event: "api_request",
    });
  }

  apiResponse(
    method: string,
    url: string,
    status: number,
    duration: number,
    context?: LogContext & { responseData?: any; responseSize?: number },
  ): void {
    const logLevel = status >= 400 ? "warn" : "debug";
    const message =
      status >= 400
        ? "API response received with error"
        : "API response received successfully";

    this.logger[logLevel](message, {
      ...context,
      method,
      url,
      status,
      duration,
      responseSize: context?.responseSize,
      responseData: context?.responseData,
      event: "api_response",
    });
  }

  apiError(
    method: string,
    url: string,
    error: Error,
    context?: LogContext,
  ): void {
    this.error("API request failed", error, {
      ...context,
      method,
      url,
      event: "api_error",
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
